
import { supabaseExtended } from "@/integrations/supabase/extendedClient";
import { toast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";

type Comment = {
  id: number | string;
  author: string;
  author_address: string;
  timestamp: string;
  content: string;
  likes: number;
  liked_by_user: boolean;
  parent_id?: number | string;
  replies?: Comment[];
};

class CommentsClient {
  async fetchComments(coinId: string, userAddress?: string): Promise<Comment[]> {
    try {
      // First check if we have a user profile for this wallet address
      let userId = null;
      if (userAddress) {
        const { data: profileData } = await supabaseExtended
          .from('profiles')
          .select('id')
          .eq('wallet_address', userAddress.toLowerCase())
          .maybeSingle();
        
        userId = profileData?.id;
      }

      // Fetch top-level comments (comments with no parent)
      const { data: commentsData, error } = await supabaseExtended
        .from('coin_comments')
        .select('*')
        .eq('coin_id', coinId)
        .is('parent_id', null)
        .order('created_at', { ascending: false });

      if (error) {
        console.error("Error fetching comments:", error);
        toast({
          title: "Error",
          description: "Failed to load comments",
          variant: "destructive",
        });
        return [];
      }

      // Fetch user profiles for the comments
      const userIds = commentsData.map(comment => comment.user_id);
      const { data: profilesData, error: profilesError } = userIds.length > 0 
        ? await supabaseExtended
            .from('profiles')
            .select('id, username, wallet_address')
            .in('id', userIds)
        : { data: [], error: null };

      if (profilesError) {
        console.error("Error fetching profiles:", profilesError);
      }

      // Create a map of user_id to profile data
      const profilesMap = (profilesData || []).reduce((acc, profile) => {
        acc[profile.id] = profile;
        return acc;
      }, {} as Record<string, any>);

      // Fetch likes for all comments
      const commentIds = commentsData.map(comment => comment.id);
      const { data: likesData, error: likesError } = commentIds.length > 0 
        ? await supabaseExtended
            .from('coin_comment_likes')
            .select('id, comment_id, user_id')
            .in('comment_id', commentIds)
        : { data: [], error: null };

      if (likesError) {
        console.error("Error fetching likes:", likesError);
      }

      // Group likes by comment_id
      const likesByComment = (likesData || []).reduce((acc, like) => {
        if (!acc[like.comment_id]) {
          acc[like.comment_id] = [];
        }
        acc[like.comment_id].push(like);
        return acc;
      }, {} as Record<string, any[]>);

      // Fetch replies for all comments
      const { data: repliesData, error: repliesError } = commentIds.length > 0 
        ? await supabaseExtended
            .from('coin_comments')
            .select('*')
            .in('parent_id', commentIds)
            .order('created_at', { ascending: true })
        : { data: [], error: null };

      if (repliesError) {
        console.error("Error fetching replies:", repliesError);
      }

      // Fetch user profiles for replies
      const replyUserIds = (repliesData || []).map(reply => reply.user_id);
      const { data: replyProfilesData, error: replyProfilesError } = replyUserIds.length > 0 
        ? await supabaseExtended
            .from('profiles')
            .select('id, username, wallet_address')
            .in('id', replyUserIds)
        : { data: [], error: null };

      if (replyProfilesError) {
        console.error("Error fetching reply profiles:", replyProfilesError);
      }

      // Create a map of user_id to profile data for replies
      const replyProfilesMap = (replyProfilesData || []).reduce((acc, profile) => {
        acc[profile.id] = profile;
        return acc;
      }, {} as Record<string, any>);

      // Fetch likes for all replies
      const replyIds = (repliesData || []).map(reply => reply.id);
      const { data: replyLikesData, error: replyLikesError } = replyIds.length > 0 
        ? await supabaseExtended
            .from('coin_comment_likes')
            .select('id, comment_id, user_id')
            .in('comment_id', replyIds)
        : { data: [], error: null };

      if (replyLikesError) {
        console.error("Error fetching reply likes:", replyLikesError);
      }

      // Group likes by reply_id
      const likesByReply = (replyLikesData || []).reduce((acc, like) => {
        if (!acc[like.comment_id]) {
          acc[like.comment_id] = [];
        }
        acc[like.comment_id].push(like);
        return acc;
      }, {} as Record<string, any[]>);

      // Format replies with profile data and likes
      const formattedReplies = (repliesData || []).map(reply => {
        const profile = replyProfilesMap[reply.user_id] || {};
        const likes = likesByReply[reply.id] || [];
        return this.formatCommentWithData(reply, profile, likes, userId, userAddress);
      });

      // Group replies by parent_id
      const repliesByParent = formattedReplies.reduce((acc, reply) => {
        const parentId = reply.parent_id;
        if (!acc[parentId]) {
          acc[parentId] = [];
        }
        acc[parentId].push(reply);
        return acc;
      }, {} as Record<string, Comment[]>);

      // Format and return the top-level comments with replies
      return commentsData.map(comment => {
        const profile = profilesMap[comment.user_id] || {};
        const likes = likesByComment[comment.id] || [];
        const formattedComment = this.formatCommentWithData(comment, profile, likes, userId, userAddress);
        formattedComment.replies = repliesByParent[comment.id] || [];
        return formattedComment;
      });
    } catch (error) {
      console.error("Failed to fetch comments:", error);
      toast({
        title: "Error",
        description: "Failed to load comments",
        variant: "destructive",
      });
      return [];
    }
  }

  private formatCommentWithData(comment: any, profile: any, likes: any[], currentUserId?: string, currentUserAddress?: string): Comment {
    const username = profile.username || 
      (profile.wallet_address 
        ? `${profile.wallet_address.substring(0, 6)}...${profile.wallet_address.substring(profile.wallet_address.length - 4)}`
        : 'Anonymous');
    
    const likesCount = likes.length || 0;
    const likedByUser = likes.some((like: any) =>
      (currentUserId && like.user_id === currentUserId) ||
      (currentUserAddress && like.author_address?.toLowerCase() === currentUserAddress.toLowerCase())
    );
    
    return {
      id: comment.id,
      parent_id: comment.parent_id,
      author: username,
      author_address: profile.wallet_address || comment.author_address || '',
      timestamp: formatDistanceToNow(new Date(comment.created_at), { addSuffix: true }),
      content: comment.content,
      likes: likesCount,
      liked_by_user: likedByUser,
    };
  }

  async postComment(
    content: string, 
    coinId: string, 
    userAddress: string | undefined
  ): Promise<Comment | null> {
    try {
      if (!userAddress) {
        console.error("Failed to post comment: No wallet address provided");
        toast({
          title: "Error",
          description: "Please connect your wallet to post comments",
          variant: "destructive",
        });
        return null;
      }
      
      if (!content.trim()) {
        console.error("Failed to post comment: Empty content");
        toast({
          title: "Error",
          description: "Comment cannot be empty",
          variant: "destructive",
        });
        return null;
      }

      // Get the user's ID from their wallet address or create a profile if it doesn't exist
      const { data: profileRows, error: userError } = await supabaseExtended
        .from('profiles')
        .select('id, username')
        .eq('wallet_address', userAddress.toLowerCase())
        .limit(1);
      let userData = profileRows?.[0] || null;

      if (userError) {
        console.warn("Profile lookup unavailable; using wallet identity:", userError);
        userData = null;
      }

      // If user doesn't exist, create a profile
      if (!userData) {
        const { data: newUserData, error: createUserError } = await supabaseExtended
          .from('profiles')
          .insert({
            wallet_address: userAddress.toLowerCase(),
            username: `${userAddress.substring(0, 6)}...${userAddress.substring(userAddress.length - 4)}`
          })
          .select('id, username')
          .single();

        if (!createUserError && newUserData) userData = newUserData;
      }

      // Insert the comment
      const { data: commentData, error: commentError } = await (supabaseExtended as any)
        .from('coin_comments')
        .insert({
          content,
          coin_id: coinId,
          user_id: userData?.id || null,
          author_address: userAddress.toLowerCase()
        })
        .select('*')
        .single();

      if (commentError || !commentData) {
        console.error("Failed to post comment:", commentError);
        toast({
          title: "Error",
          description: "Failed to post comment",
          variant: "destructive",
        });
        return null;
      }
      
      // Format the new comment
      return {
        id: commentData.id,
        author: userData?.username || userAddress.substring(0, 6) + "..." + userAddress.substring(userAddress.length - 4),
        author_address: userAddress,
        timestamp: "just now",
        content,
        likes: 0,
        liked_by_user: false,
        replies: []
      };
    } catch (error) {
      console.error("Failed to post comment:", error);
      toast({
        title: "Error",
        description: "Failed to post comment",
        variant: "destructive",
      });
      return null;
    }
  }

  async postReply(
    content: string, 
    parentId: string | number, 
    coinId: string,
    userAddress: string | undefined
  ): Promise<Comment | null> {
    try {
      if (!userAddress) {
        console.error("Failed to post reply: No wallet address provided");
        toast({
          title: "Error",
          description: "Please connect your wallet to post replies",
          variant: "destructive",
        });
        return null;
      }
      
      if (!content.trim()) {
        console.error("Failed to post reply: Empty content");
        toast({
          title: "Error",
          description: "Reply cannot be empty",
          variant: "destructive",
        });
        return null;
      }

      // Get the user's ID from their wallet address or create a profile if it doesn't exist
      const { data: profileRows, error: userError } = await supabaseExtended
        .from('profiles')
        .select('id, username')
        .eq('wallet_address', userAddress.toLowerCase())
        .limit(1);
      let userData = profileRows?.[0] || null;

      if (userError) {
        console.warn("Profile lookup unavailable; using wallet identity:", userError);
        userData = null;
      }

      // If user doesn't exist, create a profile
      if (!userData) {
        const { data: newUserData, error: createUserError } = await supabaseExtended
          .from('profiles')
          .insert({
            wallet_address: userAddress.toLowerCase(),
            username: `${userAddress.substring(0, 6)}...${userAddress.substring(userAddress.length - 4)}`
          })
          .select('id, username')
          .single();

        if (!createUserError && newUserData) userData = newUserData;
      }

      // Insert the reply
      const { data: replyData, error: replyError } = await (supabaseExtended as any)
        .from('coin_comments')
        .insert({
          content,
          coin_id: coinId,
          user_id: userData?.id || null,
          author_address: userAddress.toLowerCase(),
          parent_id: parentId
        })
        .select('*')
        .single();

      if (replyError || !replyData) {
        console.error("Failed to post reply:", replyError);
        toast({
          title: "Error",
          description: "Failed to post reply",
          variant: "destructive",
        });
        return null;
      }
      
      // Format the new reply
      return {
        id: replyData.id,
        parent_id: replyData.parent_id,
        author: userData?.username || userAddress.substring(0, 6) + "..." + userAddress.substring(userAddress.length - 4),
        author_address: userAddress,
        timestamp: "just now",
        content,
        likes: 0,
        liked_by_user: false
      };
    } catch (error) {
      console.error("Failed to post reply:", error);
      toast({
        title: "Error",
        description: "Failed to post reply",
        variant: "destructive",
      });
      return null;
    }
  }

  async toggleLike(
    commentId: string | number,
    userAddress: string | undefined
  ): Promise<{ success: boolean; liked: boolean }> {
    try {
      if (!userAddress) {
        console.error("Failed to toggle like: No wallet address provided");
        toast({
          title: "Error",
          description: "Please connect your wallet to like comments",
          variant: "destructive",
        });
        return { success: false, liked: false };
      }

      // Get the user's ID from their wallet address or create a profile if it doesn't exist
      const { data: profileRows, error: userError } = await supabaseExtended
        .from('profiles')
        .select('id')
        .eq('wallet_address', userAddress.toLowerCase())
        .limit(1);
      let userData = profileRows?.[0] || null;

      if (userError) {
        console.warn("Profile lookup unavailable; using wallet identity:", userError);
        userData = null;
      }

      // If user doesn't exist, create a profile
      if (!userData) {
        const { data: newUserData, error: createUserError } = await supabaseExtended
          .from('profiles')
          .insert({
            wallet_address: userAddress.toLowerCase(),
            username: `${userAddress.substring(0, 6)}...${userAddress.substring(userAddress.length - 4)}`
          })
          .select('id')
          .single();

        if (!createUserError && newUserData) userData = newUserData;
      }

      // Check if the user has already liked this comment
      let likeLookup = (supabaseExtended as any)
        .from('coin_comment_likes')
        .select('id')
        .eq('comment_id', commentId)
        .limit(1);
      likeLookup = userData?.id
        ? likeLookup.eq('user_id', userData.id)
        : likeLookup.eq('author_address', userAddress.toLowerCase());
      const { data: likeRows, error: checkError } = await likeLookup;
      const existingLike = likeRows?.[0] || null;

      if (checkError) {
        console.error("Failed to check like status:", checkError);
        toast({
          title: "Error",
          description: "Failed to process like",
          variant: "destructive",
        });
        return { success: false, liked: false };
      }

      if (existingLike) {
        // User has already liked the comment, so remove the like
        const { error: deleteError } = await supabaseExtended
          .from('coin_comment_likes')
          .delete()
          .eq('id', existingLike.id);

        if (deleteError) {
          console.error("Failed to unlike comment:", deleteError);
          toast({
            title: "Error",
            description: "Failed to unlike comment",
            variant: "destructive",
          });
          return { success: false, liked: true };
        }

        return { success: true, liked: false };
      } else {
        // User hasn't liked the comment yet, so add a like
        const { error: insertError } = await (supabaseExtended as any)
          .from('coin_comment_likes')
          .insert({
            comment_id: commentId,
            user_id: userData?.id || null,
            author_address: userAddress.toLowerCase()
          });

        if (insertError) {
          console.error("Failed to like comment:", insertError);
          toast({
            title: "Error",
            description: "Failed to like comment",
            variant: "destructive",
          });
          return { success: false, liked: false };
        }

        return { success: true, liked: true };
      }
    } catch (error) {
      console.error("Failed to toggle like:", error);
      toast({
        title: "Error",
        description: "Failed to process like",
        variant: "destructive",
      });
      return { success: false, liked: false };
    }
  }
}

export const commentsClient = new CommentsClient();
export type { Comment };
